import {RouteLang} from "~~/route/enum/route-lang.enum";
import type {UseSeoMetaInput} from "@unhead/vue";

export const useSeo = (options: {
    title?: string
    description?: string
    ogImage?: string
    customMeta?: UseSeoMetaInput
} = {}) => {
    const {locale, defaultLocale, t} = useI18n()
    const route = useRoute()
    const config = useRuntimeConfig().public
    // Build canonical URL - use runtime config or default
    const baseUrl = config.siteUrl || 'https://www.enerwin-aermec.be'
    const canonicalUrl = `${baseUrl}${route.fullPath}`
    // Build alternate URLs for hreflang
    const languages = Object.values(RouteLang).map(l => l.toLowerCase());
    const languagePipes: string = languages.join('|');
    const alternateUrls = languages.map(lang => {
        // Handle path replacement properly for language prefixes
        const pathWithoutLocale = route.path.replace(new RegExp(`^/(${languagePipes})`), '')
        const path = `/${lang}${pathWithoutLocale}`
        return {
            hreflang: lang,
            href: `${baseUrl}${path}`
        }
    })
    // Add x-default for international targeting
    const pathWithoutLocale = route.path.replace(/^\/(fr|en|nl)/, '')
    const defaultPath = `/${defaultLocale}${pathWithoutLocale}`
    alternateUrls.push({
        hreflang: 'x-default',
        href: `${baseUrl}${defaultPath}`
    })
    // Default values
    const defaultTitle = config.siteName
    const defaultDescription = t('seo.description')
    const title = options.title || defaultTitle
    const description = options.description || defaultDescription
    const ogImage = options.ogImage || `${baseUrl}${config.logo.horizontal}`
    // Build meta tags
    const seoMeta: UseSeoMetaInput = {
        title: `${title}`,
        description,
        ogTitle: `${title} - ${config.siteName}`,
        ogDescription: description,
        ogImage,
        ogUrl: canonicalUrl,
        ogType: 'website',
        ogLocale: locale.value,
        ogSiteName: config.siteName,
        twitterCard: 'summary_large_image',
        twitterTitle: `${title} - ${config.siteName}`,
        twitterDescription: description,
        twitterImage: ogImage,
        ...options.customMeta
    }
    useSeoMeta(seoMeta)
    // Add canonical and hreflang via useHead
    useHead({
        htmlAttrs: {
            lang: locale.value
        },
        link: [
            {
                rel: 'canonical',
                href: canonicalUrl
            },
            ...alternateUrls.map(alt => ({
                rel: 'alternate',
                hreflang: alt.hreflang,
                href: alt.href
            }))
        ]
    })
    return {
        title,
        description,
        canonicalUrl,
        alternateUrls
    }
}
